import { LocalizedText, useSourceLanguage } from "../i18n/LocalizedCopy";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";

import { useI18n } from "../i18n/LanguageContext";
import {
  createConfirmationChallenge,
  createConfirmationId,
  createConfirmationReceipt,
  downloadConfirmationCertificate,
  recordConfirmationEvent,
  verifyWithPasskey,
  verifyConfirmationSecurity,
} from "../services/actionConfirmationApi";
import "./PremiumParticipationConfirmation.css";

const VARIANT_META = Object.freeze({
  market: { brand: "MARKETPLACE PRIVÉE", symbol: "◈" },
  auction: { brand: "ENCHÈRES SIGNATURE", symbol: "♢" },
  flash: { brand: "VENTE FLASH", symbol: "ϟ" },
  sport: { brand: "SPORT & LOISIRS", symbol: "✦" },
  sortie: { brand: "SORTIE PRIVÉE", symbol: "◐" },
  crowdfunding: { brand: "CROWDFUNDING PRIVÉ", symbol: "◇" },
  voyage: { brand: "VOYAGE PRIVÉ", symbol: "✧" },
  babysitting: { brand: "BABYSITTING PROTÉGÉ", symbol: "♧" },
  animaux: { brand: "ANIMAUX PROTÉGÉS", symbol: "♡" },
  immobilier: { brand: "IMMOBILIER SIGNATURE", symbol: "⌂" },
  emploi: { brand: "CARRIÈRE PRIVÉE", symbol: "◆" },
  services: { brand: "SERVICES SÉCURISÉS", symbol: "✦" },
  forum: { brand: "COMMUNAUTÉ PRIVÉE", symbol: "◌" },
  amis: { brand: "CERCLE PRIVÉ", symbol: "◇" },
  donner: { brand: "ENTRAIDE PRIVÉE", symbol: "✺" },
  premium: { brand: "PREMIUM SIGNATURE", symbol: "♕" },
});

function remainingLabel(value, t) {
  const count = Math.max(0, Number(value || 0));
  const template = t(count === 1 ? "confirmation.available_singular" : "confirmation.available_plural", count === 1 ? "1 place disponible avant validation" : "{count} places disponibles avant validation");
  return template.replace("{count}", String(count));
}

function safeActionKey(value) {
  return String(value || "action-eternua")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 96) || "action-eternua";
}

function errorMessage(error, t) {
  const status = Number(error?.response?.status || 0);
  const data = error?.response?.data;
  if (status === 401) return t("confirmation.auth_required", "Connectez-vous pour utiliser cette validation sécurisée.");
  if (status === 403) return t("confirmation.forbidden", "Votre compte n’est pas autorisé à confirmer cette action.");
  if (status === 429) return t("confirmation.rate_limited", "Trop de tentatives. Patientez un instant avant de réessayer.");
  if (status === 400 && data?.confirmation_code) return t("confirmation.error_code", "Le code de confirmation est incorrect.");
  if (status === 400 && data?.password) return t("confirmation.error_password", "Le mot de passe du compte est incorrect.");
  if (status === 400) return t("confirmation.error_validation", "Vérifiez les informations saisies avant de réessayer.");
  return t("confirmation.error_generic", "Cette action n’a pas pu être confirmée. Réessayez dans un instant.");
}

export default function PremiumParticipationConfirmation({ "data-et-copy": __etCopy = "",
  variant = "sport",
  experienceMode = "premium",
  universe,
  title,
  category,
  date,
  location,
  organizer,
  remainingPlaces,
  priceLabel,
  itemPriceLabel,
  amount = null,
  currency = "EUR",
  reference = "",
  actionKey: actionKeyOverride = "",
  actionLabel = "",
  image,
  paid = false,
  paymentFollows = paid,
  demo = false,
  busy = false,
  error = "",
  heading: headingOverride,
  brandLabel,
  description: descriptionOverride,
  experienceEyebrow,
  unitLabel,
  summaryLabel,
  feeLabel,
  feeValue,
  totalLabel,
  paymentDetail: paymentDetailOverride,
  confirmLabel: confirmLabelOverride,
  consentLabel: consentLabelOverride,
  consentDetail: consentDetailOverride,
  availabilityLabel,
  assurances,
  extraContent,
  footnote,
  securityLevel = "standard",
  securityChallenge: externalChallenge = null,
  securityLoading: externalSecurityLoading = false,
  securityError: externalSecurityError = "",
  preferences = {},
  managedLifecycle = false,
  receiptPending = false,
  onConsentChange,
  onSecurityMethod,
  onClose,
  onConfirm,
}) {
  useSourceLanguage();
  const { lang, t } = useI18n();
  const [accepted, setAccepted] = useState(false);
  const [confirmationCode, setConfirmationCode] = useState("");
  const [totpCode, setTotpCode] = useState("");
  const [password, setPassword] = useState("");
  const [emailRequested, setEmailRequested] = useState(Boolean(preferences.email_receipts_by_default));
  const [authenticationMethod, setAuthenticationMethod] = useState(preferences.preferred_authenticator === "authenticator_app" ? "authenticator_app" : "display_code");
  const [passkeyVerification, setPasskeyVerification] = useState(null);
  const [passkeyBusy, setPasskeyBusy] = useState(false);
  const [localBusy, setLocalBusy] = useState(false);
  const [localError, setLocalError] = useState("");
  const [localChallenge, setLocalChallenge] = useState(null);
  const [localSecurityLoading, setLocalSecurityLoading] = useState(false);
  const [localSecurityError, setLocalSecurityError] = useState("");
  const [receipt, setReceipt] = useState(null);
  const [localReceiptPending, setLocalReceiptPending] = useState(false);
  const completedActionRef = useRef(null);
  const dialogRef = useRef(null);
  const closeRef = useRef(null);
  const closeHandlerRef = useRef(onClose);
  const busyRef = useRef(busy || localBusy);
  const lifecycleRef = useRef({
    sessionId: createConfirmationId("confirmation-session"),
    idempotencyKey: createConfirmationId("confirmation-receipt"),
  });
  const isSortie = variant === "sortie";
  const variantMeta = VARIANT_META[variant] || VARIANT_META.sport;
  const resolvedUnitLabel = unitLabel || t("confirmation.unit_label", "Place standard × 1");
  const resolvedSummaryLabel = summaryLabel || t("confirmation.summary_label", "Récapitulatif de la réservation");
  const resolvedFeeLabel = feeLabel || t("confirmation.fee_label", "Frais de service participant");
  const resolvedFeeValue = feeValue || t("confirmation.fee_none", "Aucun");
  const resolvedExperienceEyebrow = experienceEyebrow || t("confirmation.experience_eyebrow", "VOTRE EXPÉRIENCE");
  const resolvedUniverse = universe || variant || "premium";
  const resolvedActionKey = actionKeyOverride || safeActionKey(actionLabel || resolvedUnitLabel || confirmLabelOverride);
  const resolvedReference = reference || lifecycleRef.current.sessionId;
  const challenge = externalChallenge || localChallenge;
  const securityLoading = externalSecurityLoading || localSecurityLoading;
  const securityError = externalSecurityError || localSecurityError;
  const effectiveBusy = busy || localBusy;
  const recoveringReceipt = receiptPending || localReceiptPending;
  const requiresCode = securityLevel === "reinforced" || securityLevel === "critical";
  const requiresPassword = securityLevel === "critical" && authenticationMethod !== "passkey";

  closeHandlerRef.current = onClose;
  busyRef.current = effectiveBusy;

  const analyticsBase = useMemo(() => ({
    session_id: lifecycleRef.current.sessionId,
    universe: resolvedUniverse,
    action_key: resolvedActionKey,
    reference: resolvedReference,
    security_level: securityLevel,
  }), [resolvedActionKey, resolvedReference, resolvedUniverse, securityLevel]);

  useEffect(() => {
    if (managedLifecycle) return;
    recordConfirmationEvent({ ...analyticsBase, event_type: "opened" });
  }, [analyticsBase, managedLifecycle]);

  useEffect(() => {
    if (managedLifecycle || !requiresCode) return;
    let active = true;
    setLocalSecurityLoading(true);
    createConfirmationChallenge({
      universe: resolvedUniverse,
      action_key: resolvedActionKey,
      reference: resolvedReference,
      security_level: securityLevel,
    }).then((nextChallenge) => {
      if (active) setLocalChallenge(nextChallenge);
    }).catch((challengeError) => {
      if (active) setLocalSecurityError(errorMessage(challengeError, t));
    }).finally(() => {
      if (active) setLocalSecurityLoading(false);
    });
    return () => { active = false; };
  }, [managedLifecycle, requiresCode, resolvedActionKey, resolvedReference, resolvedUniverse, securityLevel, t]);

  useEffect(() => {
    const previouslyFocused = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const focusTimer = window.setTimeout(() => closeRef.current?.focus(), 0);
    const handleKeyDown = (event) => {
      if (event.key === "Escape" && !busyRef.current) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation?.();
        closeHandlerRef.current?.("escape");
        return;
      }
      if (event.key !== "Tab") return;
      const focusable = [...(dialogRef.current?.querySelectorAll(
        'button:not(:disabled), input:not(:disabled), [href], [tabindex]:not([tabindex="-1"])'
      ) || [])];
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      window.clearTimeout(focusTimer);
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = previousOverflow;
      previouslyFocused?.focus?.();
    };
  }, []);

  const heading = headingOverride || t(isSortie ? "confirmation.heading_participation" : "confirmation.heading_registration", isSortie ? "Confirmer votre participation" : "Confirmer votre inscription");
  const description = descriptionOverride || t("confirmation.description_default", "Vérifiez l’activité, la date et le tarif avant de poursuivre.");
  const brand = brandLabel && brandLabel !== variantMeta.brand
    ? brandLabel
    : t(`confirmation.brand_${variant}`, variantMeta.brand);
  const defaultPaymentDetail = paymentFollows
    ? t(isSortie ? "confirmation.payment_next" : "confirmation.payment_secure_next", isSortie ? "Paiement à l’étape suivante" : "Paiement sécurisé à l’étape suivante")
    : paid ? t("confirmation.price_informational", "Tarif informatif · aucun paiement déclenché") : t("confirmation.payment_none", "Aucun paiement requis");
  const paymentDetail = paymentDetailOverride || defaultPaymentDetail;
  const confirmLabel = confirmLabelOverride || t(paymentFollows ? "confirmation.confirm_continue" : "confirmation.confirm_place", paymentFollows ? "Confirmer et continuer" : "Confirmer ma place");
  const consentLabel = consentLabelOverride || t("confirmation.consent_default", "Je confirme les informations affichées.");
  const consentDetail = consentDetailOverride || t("confirmation.consent_detail", "L’action ne sera exécutée qu’après votre accord.");
  const defaultAssurances = [
    ["◇", t("confirmation.place_identified", "Place identifiée"), availabilityLabel || remainingLabel(remainingPlaces, t)],
    ["✓", t("confirmation.price_transparent", "Tarif transparent"), `${resolvedFeeLabel} : ${String(resolvedFeeValue).toLowerCase()}`],
    ["◈", t(paymentFollows ? "confirmation.payment_step" : "confirmation.direct_registration", paymentFollows ? "Étape de paiement" : "Inscription directe"), paymentDetail],
  ];
  const assuranceItems = assurances?.length ? assurances : defaultAssurances;
  const codeMatches = authenticationMethod === "passkey"
    ? Boolean(passkeyVerification?.verification_token)
    : authenticationMethod === "authenticator_app"
      ? totpCode.replace(/\D/g, "").length === 6
      : !requiresCode || (challenge?.display_code && confirmationCode.replace(/\D/g, "") === String(challenge.display_code));
  const canConfirm = recoveringReceipt || (accepted && !securityLoading && !securityError && codeMatches && (!requiresPassword || password.length > 0) && !passkeyBusy);

  const chooseSecurityMethod = (method) => {
    setAuthenticationMethod(method);
    setPasskeyVerification(null);
    onSecurityMethod?.(method);
    if (!managedLifecycle) recordConfirmationEvent({ ...analyticsBase, event_type: "security_method", authentication_method: method, step_key: `security_${method}` });
  };

  const authenticatePasskey = async () => {
    if (passkeyBusy) return;
    setPasskeyBusy(true);
    setLocalSecurityError("");
    chooseSecurityMethod("passkey");
    try {
      const verified = await verifyWithPasskey({ universe: resolvedUniverse, action_key: resolvedActionKey, reference: resolvedReference, security_level: securityLevel });
      setPasskeyVerification(verified);
    } catch (passkeyError) {
      setLocalSecurityError(errorMessage(passkeyError, t));
    } finally {
      setPasskeyBusy(false);
    }
  };

  const requestClose = (reason = "button") => {
    if (effectiveBusy) return;
    if (!managedLifecycle) recordConfirmationEvent({ ...analyticsBase, event_type: "cancelled", reason });
    onClose?.(reason);
  };

  const changeConsent = (event) => {
    const value = event.target.checked;
    setAccepted(value);
    onConsentChange?.(value);
    if (value && !managedLifecycle) recordConfirmationEvent({ ...analyticsBase, event_type: "consented" });
  };

  const confirm = async () => {
    if (!canConfirm || effectiveBusy || busyRef.current) return;
    const securityPayload = { confirmationCode, totpCode, password, emailRequested, authenticationMethod, verificationToken: passkeyVerification?.verification_token || "" };
    if (managedLifecycle) {
      onConfirm?.(securityPayload);
      return;
    }
    busyRef.current = true;
    setLocalBusy(true);
    setLocalError("");
    let verificationToken = "";
    try {
      if (!completedActionRef.current && requiresCode && !passkeyVerification?.verification_token) {
        const verified = await verifyConfirmationSecurity({
          challenge_token: challenge.challenge_token,
          confirmation_code: confirmationCode,
          totp_code: totpCode,
          authentication_method: authenticationMethod,
          password,
        });
        verificationToken = verified.verification_token;
      }
      if (!completedActionRef.current) {
        recordConfirmationEvent({ ...analyticsBase, event_type: "confirm_started" });
        const result = await onConfirm?.(securityPayload);
        if (result === false) return;
        // Retain only the successful result and the receipt request, never a password.
        // A receipt outage must not replay an already completed business action.
        completedActionRef.current = { result, payload: {
          universe: resolvedUniverse,
          action_key: resolvedActionKey,
          action_label: actionLabel || resolvedUnitLabel || confirmLabel,
          title,
          reference: resolvedReference,
          amount,
          currency,
          security_level: securityLevel,
          verification_token: passkeyVerification?.verification_token || verificationToken,
          locale: lang,
          email_requested: emailRequested,
          idempotency_key: lifecycleRef.current.idempotencyKey,
          metadata: { category, date, location, organizer, price_label: priceLabel, total_label: totalLabel },
        } };
      }
      const { result, payload } = completedActionRef.current;
      try {
        const nextReceipt = await createConfirmationReceipt(payload);
        if (!nextReceipt?.public_id) throw new Error("receipt_unavailable");
        setReceipt(nextReceipt);
        setLocalReceiptPending(false);
        recordConfirmationEvent({ ...analyticsBase, event_type: "confirmed", receipt_id: nextReceipt.public_id });
      } catch (_receiptError) {
        setLocalReceiptPending(true);
        setLocalError(t("confirmation.receipt_retry_detail", "L’action a réussi. Le reçu est indisponible : réessayez son enregistrement sans répéter l’action."));
        return;
      }
      if (result?.redirectUrl) window.location.assign(result.redirectUrl);
    } catch (confirmationError) {
      setLocalError(errorMessage(confirmationError, t));
      recordConfirmationEvent({ ...analyticsBase, event_type: requiresCode ? "security_failed" : "failed" });
    } finally {
      busyRef.current = false;
      setLocalBusy(false);
    }
  };

  if (receipt) {
    return (
      <div className={`premium-participation premium-participation--${variant}`} role="presentation">
        <div className="premium-participation__particles" aria-hidden="true">{Array.from({ length: 14 }, (_, index) => <i key={index} style={{ "--particle-index": index }} />)}</div>
        <section ref={dialogRef} className="premium-participation__card premium-participation__card--receipt" role="dialog" aria-modal="true" aria-label={t("confirmation.receipt_title", "Votre reçu Eternua")}>
          <span className="premium-participation__receiptSeal" aria-hidden="true">✓</span>
          <small>{<LocalizedText source={t("confirmation.receipt_eyebrow", "CONFIRMATION ENREGISTRÉE")} />}</small>
          <h2>{<LocalizedText source={t("confirmation.receipt_title", "Votre reçu Eternua")} />}</h2>
          <p>{<LocalizedText source={t("confirmation.receipt_description", "L’action est terminée et sa preuve est disponible dans Mes démarches.")} />}</p>
          <dl><div><dt>{<LocalizedText source={t("confirmation.reference", "Référence")} />}</dt><dd>{receipt.public_id}</dd></div><div><dt>{<LocalizedText source={t("confirmation.date", "Date et heure")} />}</dt><dd>{new Intl.DateTimeFormat(lang, { dateStyle: "medium", timeStyle: "short" }).format(new Date(receipt.confirmed_at))}</dd></div><div><dt>{<LocalizedText source={t("confirmation.security", "Niveau de sécurité")} />}</dt><dd>{receipt.security_level}</dd></div></dl>
          <div className="premium-participation__receiptActions"><Link to={`/mes-eternua/demarches?confirmation=${encodeURIComponent(receipt.public_id)}`}>{<LocalizedText source={t("confirmation.history", "Voir dans Mes démarches")} />}</Link><button type="button" onClick={() => downloadConfirmationCertificate(receipt.public_id)}>{<LocalizedText source={t("confirmation.pdf", "Télécharger le PDF certifié")} />}</button><button type="button" onClick={() => onClose?.("done")}>{<LocalizedText source={t("confirmation.done", "Terminer")} />}</button></div>
        </section>
      </div>
    );
  }

  return (
    <div className={`premium-participation premium-participation--${variant} premium-participation--experience-${experienceMode} premium-participation--security-${securityLevel} premium-participation--motion-${preferences.animation_intensity || "full"} premium-participation--contrast-${preferences.contrast_mode || "standard"} premium-participation--controls-${preferences.control_size || "standard"}`} role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) requestClose("backdrop"); }}>
      <div className="premium-participation__particles" aria-hidden="true">{Array.from({ length: 14 }, (_, index) => <i key={index} style={{ "--particle-index": index }} />)}</div>
      <section ref={dialogRef} className="premium-participation__card" role="dialog" aria-modal="true" aria-labelledby="premium-participation-title" aria-describedby="premium-participation-description" onMouseDown={(event) => event.stopPropagation()}>
        <div className="premium-participation__aura" aria-hidden="true" />
        <header className="premium-participation__header">
          <div className="premium-participation__brand" aria-label={`Eternua ${brand}`}><span aria-hidden="true">E</span><p><strong><LocalizedText source={"ETERNUA"} /></strong><small>{brand}</small></p></div>
          <button ref={closeRef} type="button" onClick={() => requestClose("button")} disabled={effectiveBusy} aria-label={`${t("confirmation.close", "Fermer")} : ${heading}`}>×</button>
        </header>

        <div className="premium-participation__intro"><span><i aria-hidden="true" /> {<LocalizedText source={t("confirmation.last_check", "Dernière vérification")} />}</span><h2 id="premium-participation-title">{heading}</h2><p id="premium-participation-description">{description}</p></div>

        {externalChallenge?.risk?.score > 0 ? <div className="premium-participation__risk" role="status"><strong>{<LocalizedText source={t("confirmation.risk_check", "Contrôle de sécurité adapté")} />}</strong><span>{<LocalizedText source={t("confirmation.risk_detail", "Eternua a renforcé cette validation selon le montant, l’appareil ou le rythme d’activité.")} />}</span></div> : null}

        <div className="premium-participation__experience"><div className={`premium-participation__visual${image ? " has-image" : ""}`}>{image ? <img src={image} alt="" /> : <span aria-hidden="true">{<LocalizedText source={variantMeta.symbol} />}</span>}<b>{category || <LocalizedText source={t(isSortie ? "confirmation.category_outing" : "confirmation.category_activity", isSortie ? "Sortie" : "Activité")} />}</b></div><div className="premium-participation__experienceCopy"><small>{resolvedExperienceEyebrow}</small><h3>{<LocalizedText source={title} marked={__etCopy.split(',').includes("title")} />}</h3><p><span aria-hidden="true">▣</span> {date}</p><p><span aria-hidden="true">⌖</span> {location}</p><em>{<LocalizedText source={t("confirmation.organized_by", "Organisé par")} />} <strong>{organizer || <LocalizedText source={"Eternua"} />}</strong></em></div></div>

        <div className="premium-participation__receipt" aria-label={resolvedSummaryLabel}><div><span>{resolvedUnitLabel}</span><strong>{itemPriceLabel || priceLabel}</strong></div><div><span>{resolvedFeeLabel}</span><strong>{resolvedFeeValue}</strong></div><div className="premium-participation__total"><span>{totalLabel || <LocalizedText source={t(paid ? "confirmation.amount_label" : "confirmation.total_label", paid ? "Montant de la place" : "Total")} />}<small>{paymentDetail}</small></span><strong>{priceLabel}</strong></div></div>

        <div className="premium-participation__assurances" aria-label={t("confirmation.info_label", "Informations avant confirmation")}>{assuranceItems.map(([icon, assuranceTitle, detail]) => <div key={assuranceTitle}><span aria-hidden="true">{icon}</span><p><strong>{assuranceTitle}</strong><small>{detail}</small></p></div>)}</div>

        {extraContent ? <div className="premium-participation__extra">{extraContent}</div> : null}

        <label className="premium-participation__consent"><input type="checkbox" checked={accepted} onChange={changeConsent} /><span aria-hidden="true" /><p><strong>{consentLabel}</strong><small>{consentDetail}</small></p></label>

        {requiresCode ? <section className="premium-participation__security" aria-label={t(`confirmation.security_${securityLevel}`, "Validation renforcée")}><header><span aria-hidden="true">{requiresPassword ? "⌘" : "#"}</span><div><strong>{<LocalizedText source={t(`confirmation.security_${securityLevel}`, requiresPassword ? "Double validation critique" : "Validation renforcée")} />}</strong><small>{<LocalizedText source={t("confirmation.security_help_webauthn", "Choisissez une méthode de vérification forte et accessible.")} />}</small></div></header>{securityLoading ? <p className="premium-participation__securityLoading" aria-live="polite">{<LocalizedText source={t("confirmation.security_loading", "Préparation de la validation sécurisée…")} />}</p> : challenge ? <><div className="premium-participation__securityMethods" role="group" aria-label={t("confirmation.security_method", "Méthode de vérification")}>
          {challenge.available_methods?.passkey ? <button type="button" className={authenticationMethod === "passkey" ? "is-active" : ""} onClick={authenticatePasskey} disabled={passkeyBusy}><span aria-hidden="true">◉</span>{passkeyBusy ? <LocalizedText source={t("confirmation.passkey_wait", "Vérification…")} /> : <LocalizedText source={t("confirmation.passkey", "Face ID, empreinte ou Passkey")} />}</button> : <Link to="/mfa-setup">{<LocalizedText source={t("confirmation.add_passkey", "Ajouter une Passkey")} />}</Link>}
          {challenge.available_methods?.authenticator_app ? <button type="button" className={authenticationMethod === "authenticator_app" ? "is-active" : ""} onClick={() => chooseSecurityMethod("authenticator_app")}><span aria-hidden="true">⌁</span>{<LocalizedText source={t("confirmation.authenticator_app", "Application d’authentification")} />}</button> : null}
          <button type="button" className={authenticationMethod === "display_code" ? "is-active" : ""} onClick={() => chooseSecurityMethod("display_code")}><span aria-hidden="true">Aa</span>{<LocalizedText source={t("confirmation.accessible_fallback", "Alternative accessible")} />}</button>
        </div>
        {authenticationMethod === "passkey" ? <div className={`premium-participation__passkeyState${passkeyVerification ? " is-valid" : ""}`} role="status">{passkeyVerification ? <LocalizedText source={t("confirmation.passkey_verified", "Identité vérifiée cryptographiquement sur cet appareil.")} /> : <LocalizedText source={t("confirmation.passkey_prompt", "Utilisez le bouton ci-dessus pour lancer Face ID, l’empreinte ou votre Passkey.")} />}</div> : null}
        {authenticationMethod === "authenticator_app" ? <label><span>{<LocalizedText source={t("confirmation.authenticator_code", "Code de l’application")} />}</span><input inputMode="numeric" autoComplete="one-time-code" maxLength="6" value={totpCode} onChange={(event) => setTotpCode(event.target.value.replace(/\D/g, "").slice(0, 6))} placeholder={t("confirmation.code_input", "Saisir le code à 6 chiffres")} /></label> : null}
        {authenticationMethod === "display_code" ? <><div className="premium-participation__challenge"><span>{<LocalizedText source={t("confirmation.code_hint", "Code anti-erreur affiché")} />}</span><strong aria-label={t("confirmation.code_label", "Code temporaire")}>{challenge.display_code}</strong></div><label><span>{<LocalizedText source={t("confirmation.code_label", "Code temporaire")} />}</span><input inputMode="numeric" autoComplete="one-time-code" maxLength="6" value={confirmationCode} onChange={(event) => setConfirmationCode(event.target.value.replace(/\D/g, "").slice(0, 6))} placeholder={t("confirmation.code_input", "Saisir le code à 6 chiffres")} /></label></> : null}
        {requiresPassword ? <label><span>{<LocalizedText source={t("confirmation.password", "Mot de passe du compte")} />}</span><input type="password" autoComplete="current-password" value={password} onChange={(event) => setPassword(event.target.value)} placeholder={t("confirmation.password_input", "Saisir votre mot de passe")} /></label> : null}</> : null}{securityError ? <p className="premium-participation__error" role="alert">{securityError || <LocalizedText source={t("confirmation.security_unavailable", "Validation indisponible.")} />}</p> : null}</section> : null}

        <label className="premium-participation__email"><input type="checkbox" checked={emailRequested} onChange={(event) => setEmailRequested(event.target.checked)} /><span><strong>{<LocalizedText source={t("confirmation.email_label", "Recevoir aussi ce reçu par e-mail")} />}</strong><small>{<LocalizedText source={t("confirmation.email_detail", "Uniquement vers l’adresse vérifiée de votre compte.")} />}</small></span></label>

        {error || localError ? <p className="premium-participation__error" role="alert">{error || localError}</p> : null}

        <footer className="premium-participation__actions"><button type="button" onClick={() => requestClose("back")} disabled={effectiveBusy}>{<LocalizedText source={t("confirmation.back", "Retour")} />}</button><button type="button" className="is-primary" onClick={confirm} disabled={!canConfirm || effectiveBusy}><span>{effectiveBusy ? <LocalizedText source={t("confirmation.processing", "Validation en cours…")} /> : recoveringReceipt ? <LocalizedText source={t("confirmation.retry_receipt", "Réessayer le reçu")} /> : confirmLabel}</span><strong>{priceLabel}</strong><i aria-hidden="true">→</i></button></footer>

        <p className="premium-participation__footnote"><span aria-hidden="true">✶</span> {footnote || <LocalizedText source={t(demo || paymentFollows ? "confirmation.footnote_demo" : "confirmation.footnote_server", demo || paymentFollows ? "Environnement de test · Aucun débit bancaire réel" : "Confirmation serveur · Aucun paiement requis")} />}</p>
        <p className="premium-participation__analyticsNote">{<LocalizedText source={t("confirmation.analytics_private", "Mesure privée du parcours · aucune donnée sensible collectée")} />}</p>
        {preferences.screen_reader_detail ? <p className="premium-participation__srOnly">{<LocalizedText source={t("confirmation.sr_summary", `Fenêtre de confirmation pour ${actionLabel || title}. Niveau de sécurité ${securityLevel}.`)} />}</p> : null}
      </section>
    </div>
  );
}
